import { FC, useCallback, useEffect, useMemo, useState } from "react";

import PromptModal from "components/pages/Questionnaire/components/Shared/PromptModal";
import { Depend, Question } from "components/pages/Questionnaire/constants/questions";
import { PromptModalType } from "enums/questionnaire";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import ReactSelect from "react-select";
import { IUser } from "types/auth";
import { checkDepends, formatRawAddress } from "utils/new-questionnaire";

type SelectOneOptionDropdownQuestionProps = {
    user: IUser;
    data: Question;
    onAnswer: (_question: string, _answer: string | undefined) => void;
    initialValue?: any;
    categoryAnswers?: Record<string, any>;
};

const SelectOneOptionDropdownQuestion: FC<SelectOneOptionDropdownQuestionProps> = ({
    user,
    data,
    onAnswer,
    initialValue,
    categoryAnswers,
}) => {
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [showModalOpener, setShowModalOpener] = useState(false);
    const userAddress = useMemo(() => formatRawAddress(user.address), [user.address]);

    const promptModalData = useMemo(
        () => ({
            type: data.promptModal?.type || PromptModalType.info,
            title: data.promptModal?.title || "",
            text: data.promptModal?.text || "",
            image: data.promptModal?.image || "",
            promptActionButtonText: data.promptModal?.promptActionButtonText || "",
            promptActionButtonUrl: data.promptModal?.promptActionButtonUrl || "",
        }),
        [data.promptModal]
    );

    const {
        isOpened: isQuestionHelpModalOpened,
        open: handleQuestionHelpOpenModal,
        close: handleQuestionHelpCloseModal,
    } = useModalOpener();

    const getOptionText = useCallback(
        (option: string) => {
            if (data.id === "SELF_EMPLOYMENT_ADDRESS_SELECT" && option === "Account address") {
                return userAddress;
            }
            return option;
        },
        [data.id, userAddress]
    );

    const sortedOptions = useMemo(() => {
        if (!data.options) return [];
        return [...data.options].sort((a, b) =>
            getOptionText(a).localeCompare(getOptionText(b), undefined, { sensitivity: "base" })
        );
    }, [data.options, getOptionText]);

    const reactSelectOptions = useMemo(
        () =>
            sortedOptions.map(opt => ({
                label: getOptionText(opt),
                value: opt,
            })),
        [sortedOptions, getOptionText]
    );

    const handleOptionChange = (selected: any) => {
        setSelectedOption(selected);
        onAnswer(data.question, selected?.value || "");
    };

    const checkModalDepends = useCallback(
        (depend: Depend[], categoryAnswers?: Record<string, any>) => {
            return categoryAnswers ? checkDepends(depend, categoryAnswers) : false;
        },
        []
    );

    useEffect(() => {
        if (initialValue) {
            const initial = reactSelectOptions.find(o => o.value === initialValue);
            setSelectedOption(initial || null);
        }
    }, [initialValue, reactSelectOptions]);

    useEffect(() => {
        if (data?.promptModal?.depend && categoryAnswers) {
            const shouldModalShow = checkModalDepends(data.promptModal?.depend, categoryAnswers);
            setShowModalOpener(shouldModalShow);
            if (shouldModalShow && !data.promptModal?.manualOpen) {
                handleQuestionHelpOpenModal();
            }
        }
    }, [categoryAnswers, checkModalDepends, data.promptModal, handleQuestionHelpOpenModal]);

    return (
        <div className="new-questionnaire__question">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title">{data.questionTitle}</div>
            )}

            <div
                className="new-questionnaire__question-text"
                style={data.questionTitle ? { margin: "4px auto" } : {}}
            >
                <span>
                    {data.question}
                    {showModalOpener && (
                        <span className="shared__learn-more" onClick={handleQuestionHelpOpenModal}>
              &nbsp;Learn more
                        </span>
                    )}
                </span>
            </div>

            {data.infoText && <div className="new-questionnaire__info-text">{data.infoText}</div>}

            <div className="new-questionnaire__select-one-option__question-options mt10">
                <ReactSelect
                    options={reactSelectOptions}
                    value={selectedOption}
                    onChange={handleOptionChange}
                    placeholder="Select an option"
                    isClearable
                    styles={{
                        container: provided => ({
                            ...provided,
                            width: "100%",
                        }),
                        control: provided => ({
                            ...provided,
                            height: 48,
                            borderRadius: 6,
                            padding: "0 4px",
                            fontSize: 16,
                        }),
                        menu: provided => ({
                            ...provided,
                            zIndex: 9999,
                        }),
                    }}
                />
            </div>

            {isQuestionHelpModalOpened && (
                <PromptModal
                    isOpen={isQuestionHelpModalOpened}
                    closeModal={handleQuestionHelpCloseModal}
                    handlePrev={() => {}}
                    data={promptModalData}
                />
            )}
        </div>
    );
};

export default SelectOneOptionDropdownQuestion;
